// src/components/GlobalSidebar.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  FiList, 
  FiCalendar, 
  FiBarChart, 
  FiFilter, 
  FiFolder,
  FiBriefcase,
  FiUser,
  FiHome,
  FiTrello,
  FiGrid,
  FiSearch
} from "react-icons/fi";
import type { ID, Project, Company, Priority } from "../../types/domain";
import { useSupabase } from "../../hooks/useSupabase";
import "../GlobalSidebar.css";

interface GlobalSidebarProps {
  // Filtros actuales
  selectedProjectId?: ID | "";
  selectedCompanyId?: ID | "";
  priorityFilter?: Priority | "";
  assigneeFilter?: ID | "";
  searchTerm?: string;
  
  // Callbacks para cambios
  onProjectChange?: (projectId: ID | "") => void;
  onCompanyChange?: (companyId: ID | "") => void;
  onPriorityChange?: (priority: Priority | "") => void;
  onAssigneeChange?: (assigneeId: ID | "") => void;
  onSearchChange?: (term: string) => void;
  onClearFilters?: () => void;
  
  // Datos adicionales
  projects?: Project[];
  companies?: Company[];
  tasks?: any[];
  
  // Opciones específicas por vista
  showFilters?: boolean;
  showProjectStats?: boolean;
}

export default function GlobalSidebar({
  selectedProjectId = "",
  selectedCompanyId = "",
  priorityFilter = "",
  assigneeFilter = "",
  searchTerm = "",
  onProjectChange,
  onCompanyChange,
  onPriorityChange,
  onAssigneeChange,
  onSearchChange,
  onClearFilters,
  projects = [],
  companies = [],
  tasks = [],
  showFilters = true,
  showProjectStats = true
}: GlobalSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  
  const [localProjects, setLocalProjects] = useState<Project[]>(projects);
  const [localCompanies, setLocalCompanies] = useState<Company[]>(companies);
  const [assigneeOptions, setAssigneeOptions] = useState<ID[]>([]);

  // Determinar la vista actual
  const getCurrentView = () => {
    const path = location.pathname;
    if (path.includes('/companies')) return 'companies';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/calendar')) return 'calendar';
    if (path.includes('/kanban')) return 'kanban';
    if (path.includes('/gantt')) return 'gantt';
    if (path.includes('/list')) return 'list';
    if (path.includes('/dashboard')) return 'dashboard';
    return 'home';
  };

  const currentView = getCurrentView();

  // Cargar datos si no se proporcionan
  const loadProjectsAndCompanies = useCallback(async () => {
    if (!supabase || (projects.length > 0 && companies.length > 0)) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        if (projects.length === 0) {
          const { data: allProjects } = await supabase
            .from("projects")
            .select("*")
            .order('name');

          if (allProjects) {
            setLocalProjects(allProjects as Project[]);
          }
        }

        if (companies.length === 0) {
          const { data: allCompanies } = await supabase
            .from("companies")
            .select("*")
            .order('name');

          if (allCompanies) {
            setLocalCompanies(allCompanies as Company[]);
          }
        }
      }
    } catch (error) {
      console.error("Error cargando proyectos y empresas:", error);
    }
  }, [supabase, projects.length, companies.length]);

  useEffect(() => {
    loadProjectsAndCompanies();
  }, [loadProjectsAndCompanies]);

  // Usar props o datos locales
  const displayProjects = projects.length > 0 ? projects : localProjects;
  const displayCompanies = companies.length > 0 ? companies : localCompanies;

  // Generar opciones de asignados
  useEffect(() => {
    if (tasks.length > 0) {
      const uniqueAssignees = new Set(
        tasks.map((t: any) => t.assignee_id).filter(Boolean)
      );
      setAssigneeOptions(Array.from(uniqueAssignees) as ID[]);
    }
  }, [tasks]);

  // Navegación
  const navigationItems = [
    { id: 'dashboard', label: 'Inicio', icon: FiHome, path: '/' },
    { id: 'kanban', label: 'Kanban', icon: FiTrello, path: '/kanban' },
    { id: 'calendar', label: 'Calendario', icon: FiCalendar, path: '/calendar-v2' },
    { id: 'list', label: 'Lista', icon: FiList, path: '/list-v2' },
    { id: 'gantt', label: 'Gantt', icon: FiBarChart, path: '/gantt' },
    { id: 'companies', label: 'Empresas', icon: FiBriefcase, path: '/companies' },
    { id: 'profile', label: 'Mi Perfil', icon: FiUser, path: '/profile' },
  ];

  const handleProjectSelect = (newProjectId: ID | "") => {
    if (onProjectChange) {
      onProjectChange(newProjectId);
    }
    if (newProjectId) {
      // Find the project's company and set it
      const project = displayProjects.find(p => p.id === newProjectId);
      if (project && project.company_id && onCompanyChange) {
        onCompanyChange(project.company_id);
      }
    }
  };

  const isFiltering = 
    priorityFilter !== "" ||
    assigneeFilter !== "" ||
    (searchTerm ?? "").trim().length > 0;

  return (
    <div className="global-sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <FiGrid size={24} />
          <span>TaskManager</span>
        </div>
      </div>

      {/* Navegación principal */}
      <nav className="sidebar-nav">
        {navigationItems.map(item => (
          <button
            key={item.id}
            className={`nav-item ${currentView === item.id ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            <item.icon size={16} />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Búsqueda global */}
      {onSearchChange && (
        <div className="sidebar-section">
          <div className="section-header">
            <span>Buscar</span>
          </div>
          <div className="section-content">
            <div className="search-section">
              <div className="search-container">
                <FiSearch size={14} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar tareas..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sección de empresas */}
      {displayCompanies.length > 0 && (
        <div className="sidebar-section">
          <div className="section-header">
            <FiBriefcase size={14} />
            <span>Empresas</span>
          </div>
          <div className="section-content">
            <button
              className={`filter-item ${selectedCompanyId === "" ? "active" : ""}`}
              onClick={() => onCompanyChange?.("")}
            >
              <span>Todas las empresas</span>
              <span className="count">
                {displayCompanies.length}
              </span>
            </button>
            {displayCompanies.map(company => (
              <button
                key={company.id}
                className={`filter-item ${selectedCompanyId === company.id ? "active" : ""}`}
                onClick={() => onCompanyChange?.(company.id)}
              >
                <span>{company.name}</span>
                <span className="count">
                  {displayProjects.filter(p => p.company_id === company.id).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sección de proyectos */}
      {displayProjects.length > 0 && (
        <div className="sidebar-section">
          <div className="section-header">
            <FiFolder size={14} />
            <span>Proyectos</span>
          </div>
          <div className="section-content">
            <button
              className={`filter-item ${selectedProjectId === "" ? "active" : ""}`}
              onClick={() => handleProjectSelect("")}
            >
              <span>Todos los proyectos</span>
              <span className="count">
                {selectedCompanyId ? 
                  displayProjects.filter(p => p.company_id === selectedCompanyId).length : 
                  displayProjects.length
                }
              </span>
            </button>
            {(selectedCompanyId ? 
              displayProjects.filter(p => p.company_id === selectedCompanyId) : 
              displayProjects
            ).map(project => (
              <button
                key={project.id}
                className={`filter-item ${selectedProjectId === project.id ? "active" : ""}`}
                onClick={() => handleProjectSelect(project.id)}
              >
                <span>{project.name}</span>
                {showProjectStats && (
                  <span className="count">
                    {tasks.filter((t: any) => t.project_id === project.id).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtros (solo si se habilitan) */}
      {showFilters && (
        <div className="sidebar-section">
          <div className="section-header">
            <FiFilter size={14} />
            <span>Filtros</span>
          </div>
          <div className="section-content">
            {/* Filtro de prioridad */}
            <div className="filter-group">
              <label className="filter-label">Prioridad</label>
              <select
                className="filter-select"
                value={priorityFilter}
                onChange={(e) => onPriorityChange?.((e.target.value as Priority | "") || "")}
              >
                <option value="">Todas</option>
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>

            {/* Filtro de asignado */}
            {assigneeOptions.length > 0 && (
              <div className="filter-group">
                <label className="filter-label">Asignado</label>
                <select
                  className="filter-select"
                  value={assigneeFilter}
                  onChange={(e) => onAssigneeChange?.((e.target.value as ID) || "")}
                >
                  <option value="">Todos</option>
                  {assigneeOptions.map((uid) => (
                    <option key={uid} value={uid}>
                      {uid}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Búsqueda */}
            {onSearchChange && (
              <div className="filter-group">
                <label className="filter-label">Buscar</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Buscar tareas..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </div>
            )}

            {/* Limpiar filtros */}
            {isFiltering && onClearFilters && (
              <button className="clear-filters-btn" onClick={onClearFilters}>
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
